import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import debounce from 'lodash.debounce';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  startAfter,
  doc,
  getDoc
} from 'firebase/firestore';
import ArtListing from '../components/ArtListing';
import FilterModal from '../components/FilterModal';
import { getFilterLabel, priceRanges } from '../components/filterData';
import MarketplaceBanner from '../components/MarketplaceBanner';
import client, { ITEMS_PER_PAGE, isAlgoliaConfigured } from '../config/algolia';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
`;

const SearchContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const FilterContainer = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  gap: 10px;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #ccc;
  background: white;
  cursor: pointer;
  font-size: 0.9rem;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const ArtGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
`;

const LoadMoreButton = styled.button`
  margin: 2rem auto;
  display: block;
  padding: 0.8rem 2rem;
  background-color: #FF1493;
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  
  &:hover {
    background-color: #ff0080;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1.2rem;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #666;
`;

const FilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #f0f0f0;
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
  margin: 0 0.5rem 0.5rem 0;
  font-size: 0.9rem;
`;

const RemoveFilterButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  margin-left: 0.5rem;
  font-size: 0.9rem;
  color: #666;
  
  &:hover {
    color: #FF1493;
  }
`;

const ActiveFiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

// Transform Algolia record to match the structure expected by ArtListing
const transformAlgoliaRecord = (record) => {
  return {
    id: record.objectID, // Algolia stores the document ID as objectID
    ...record,
    artistData: {
      id: record['artistData.id'],
      displayName: record['artistData.displayName'],
      city: record['artistData.city'],
      stripeConnectAccountStatus: record['artistData.stripeConnectAccountStatus']
    }
  };
};

const MarketplaceScreen = () => {
  const [artworks, setArtworks] = useState([]);
  const [filteredArtworks, setFilteredArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const nextPageRef = useRef(0);
  const hasMoreItemsRef = useRef(true);
  const isFetchingRef = useRef(false);
  const lastVisibleRef = useRef(null);
  const [activeFilters, setActiveFilters] = useState(null);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [activeFilterType, setActiveFilterType] = useState(null);
  const navigate = useNavigate();

  const removeFilter = (type, id) => {
    if (!activeFilters) return;

    const updatedFilters = { ...activeFilters };
    updatedFilters[type] = updatedFilters[type].filter(filterId => filterId !== id);

    if (Object.values(updatedFilters).every(arr => arr.length === 0)) {
      setActiveFilters(null);
    } else {
      setActiveFilters(updatedFilters);
    }
  };

  const fetchArtworks = useCallback(async (options = {}) => {
    const {
      loadMore = false,
      refresh = false,
    } = options;

    const noMoreItems = !loadMore && !refresh && !hasMoreItemsRef.current;
    if (isFetchingRef.current || noMoreItems) return;

    try {
      setLoadingMore(loadMore);
      let artworksList = [];

      if (isAlgoliaConfigured) {
        // Use Algolia search
        const facetFilters = [
          'isForSale:true',
          'artistData.stripeConnectAccountStatus:active',
        ];
        const numericFilters = [];

        if (activeFilters) {
          if (activeFilters.cities && activeFilters.cities.length > 0) {
            const cityFilters = activeFilters.cities.map((id) => `artistData.city:cities/${id}`);
            facetFilters.push(cityFilters);
          }

          if (activeFilters.categories && activeFilters.categories.length > 0) {
            const categoryFilters = activeFilters.categories.map((id) => `categories:art_categories/${id}`);
            facetFilters.push(categoryFilters);
          }

          if (activeFilters.priceRanges && activeFilters.priceRanges.length > 0) {
            const priceFilters = priceRanges
              .filter((range, index) => activeFilters.priceRanges.includes(index))
              .map(({ min, max }) => `price:${min} TO ${max}`);
            numericFilters.push(priceFilters);
          }
        }

        let page = 0;
        if (loadMore && nextPageRef.current) {
          page = nextPageRef.current;
        }

        const {
          hits: artworksResults,
          page: resultsPage,
          nbPages,
        } = await client.searchSingleIndex({
          indexName: 'artworks_search',
          searchParams: {
            query: search,
            restrictSearchableAttributes: ['artistData.displayName'],
            facetFilters,
            numericFilters,
            hitsPerPage: ITEMS_PER_PAGE,
            page,
          },
        });

        artworksList = artworksResults.map(transformAlgoliaRecord);
        nextPageRef.current = resultsPage + 1;
        hasMoreItemsRef.current = resultsPage < nbPages;
      } else {
        // Fallback to Firestore
        const artworksRef = collection(db, 'artworks');
        let q = query(
          artworksRef,
          where('isForSale', '==', true),
          where('artistData.stripeConnectAccountStatus', '==', 'active')
        );

        if (activeFilters) {
          if (activeFilters.cities && activeFilters.cities.length > 0) {
            const cityRefs = activeFilters.cities.map(id => doc(db, 'cities', id));
            q = query(q, where('artistData.city', 'in', cityRefs));
          }

          if (activeFilters.categories && activeFilters.categories.length > 0) {
            const categoryRefs = activeFilters.categories.map(id => doc(db, 'art_categories', id));
            q = query(q, where('categories', 'array-contains-any', categoryRefs));
          }
        }

        q = query(q, orderBy('isCurated', 'desc'), orderBy('randomOrder', 'asc'));

        if (loadMore && lastVisibleRef.current) {
          q = query(q, startAfter(lastVisibleRef.current));
        }

        q = query(q, limit(ITEMS_PER_PAGE));

        const artworksSnapshot = await getDocs(q);

        if (artworksSnapshot.empty) {
          hasMoreItemsRef.current = false;
          setLoadingMore(false);
          return;
        }

        lastVisibleRef.current = artworksSnapshot.docs[artworksSnapshot.docs.length - 1];
        artworksList = artworksSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Apply price range filters in memory
        if (activeFilters?.priceRanges && activeFilters?.priceRanges?.length > 0) {
          artworksList = artworksList.filter(artwork => {
            return activeFilters.priceRanges.some(rangeIndex => {
              const range = priceRanges[rangeIndex];
              return artwork.price >= range.min && artwork.price < range.max;
            });
          });
        }

        // Apply search filter in memory
        if (search) {
          const lowercasedSearch = search.toLowerCase();
          artworksList = artworksList.filter(artwork =>
            artwork.title?.toLowerCase().includes(lowercasedSearch) ||
            (artwork.artistData && artwork.artistData.displayName?.toLowerCase().includes(lowercasedSearch))
          );
        }
      }

      setArtworks(prevArtworks =>
        refresh ? artworksList : (loadMore ? [...prevArtworks, ...artworksList] : artworksList)
      );
    } catch (error) {
      console.error("Error fetching artworks:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, [activeFilters, search]);

  // Initial fetch and refresh handling
  useEffect(() => {
    setLoading(true);
    setArtworks([]);
    nextPageRef.current = 0;
    hasMoreItemsRef.current = true;
    fetchArtworks({ refresh: true });
  }, [fetchArtworks]);

  // Search handling with debounce
  useEffect(() => {
    const debouncedSearch = debounce((searchTerm) => {
      fetchArtworks({ loadMore: false, refresh: true });
    }, 500);

    debouncedSearch(search);

    return () => {
      debouncedSearch.cancel();
    };
  }, [search, fetchArtworks]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMoreItemsRef.current && !isFetchingRef.current) {
      fetchArtworks({ loadMore: true });
    }
  };

  const handleArtworkClick = (artwork) => {
    navigate(`/artwork/${artwork.id}`);
  };

  return (
    <Container>
      <MarketplaceBanner />
      <SearchContainer>
        <SearchInput
          placeholder="Search artworks or artists..."
          value={search}
          onChange={handleSearch}
        />
      </SearchContainer>

      <FilterContainer>
        <FilterButton onClick={() => {
          setActiveFilterType('cities');
          setFilterModalVisible(true);
        }}>
          Cities
        </FilterButton>
        <FilterButton onClick={() => {
          setActiveFilterType('priceRanges');
          setFilterModalVisible(true);
        }}>
          Price
        </FilterButton>
        <FilterButton onClick={() => {
          setActiveFilterType('categories');
          setFilterModalVisible(true);
        }}>
          Categories
        </FilterButton>
      </FilterContainer>

      {activeFilters && (
        <ActiveFiltersContainer>
          {Object.entries(activeFilters).map(([type, ids]) =>
            ids.map(id => (
              <FilterChip key={`${type}-${id}`}>
                {getFilterLabel(type, id)}
                <RemoveFilterButton onClick={() => removeFilter(type, id)}>
                  ✕
                </RemoveFilterButton>
              </FilterChip>
            ))
          )}
        </ActiveFiltersContainer>
      )}

      {loading ? (
        <LoadingIndicator>Loading...</LoadingIndicator>
      ) : (
        <>
          <ArtGrid>
            {artworks.map(artwork => (
              <ArtListing
                key={artwork.id}
                artwork={artwork}
                onClick={() => handleArtworkClick(artwork)}
              />
            ))}
          </ArtGrid>
          
          {artworks.length === 0 && (
            <EmptyMessage>No artworks found</EmptyMessage>
          )}

          {hasMoreItemsRef.current && !loadingMore && (
            <LoadMoreButton onClick={handleLoadMore}>
              Load More
            </LoadMoreButton>
          )}

          {loadingMore && (
            <LoadingIndicator>Loading more...</LoadingIndicator>
          )}
        </>
      )}

      <FilterModal
        isVisible={filterModalVisible}
        onClose={() => {
          setFilterModalVisible(false);
          setActiveFilterType(null);
        }}
        onApplyFilters={setActiveFilters}
        activeFilters={activeFilters}
        filterType={activeFilterType}
      />
    </Container>
  );
};

export default MarketplaceScreen;
  